import { canAfford, getAge, visibleTechs } from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';
import { CostChips, EffectChips } from '../Chips';
import { Glyph } from '../Glyph';

interface TechPanelProps {
  snapshot: Snapshot;
}

export function TechPanel({ snapshot }: TechPanelProps) {
  const { state } = snapshot;
  const techs = visibleTechs(state);
  const capstoneId = getAge(state.ageIndex).capstoneTech;

  const known = techs.filter((view) => view.owned);
  const open = techs.filter((view) => !view.owned && view.available);
  const distant = techs.filter((view) => !view.owned && !view.available);

  return (
    <section className="panel">
      <h2 className="panel-title">
        Discoveries
        <span className="hint">
          {known.length} of {techs.length} known this generation
        </span>
      </h2>

      {open.length === 0 && distant.length === 0 ? (
        <p className="empty-note">Everything this age has to teach is already known.</p>
      ) : null}

      <div className="card-grid">
        {[...open, ...distant].map((view) => {
          const affordable = view.available && canAfford(state, view.def.cost);
          const isCapstone = view.def.id === capstoneId || view.def.isCapstone;

          return (
            <article
              key={view.def.id}
              className={`card${affordable ? ' affordable' : ''}${
                view.available ? '' : ' locked'
              }`}
            >
              <div className="card-head">
                <Glyph id={view.def.glyph} size={24} />
                <span className="card-name">{view.def.name}</span>
                {isCapstone ? <span className="effect-chip">Capstone</span> : null}
              </div>

              <p className="card-desc">{view.def.description}</p>
              <p className="card-flavor">{view.def.flavor}</p>
              <EffectChips effects={view.def.effects} />
              <CostChips cost={view.def.cost} held={state.resources} />

              {view.missingRequirements.length > 0 ? (
                <p className="card-note">Needs {view.missingRequirements.join(', ')}</p>
              ) : null}

              <div className="card-actions">
                <button
                  type="button"
                  className={`btn${affordable ? ' primary' : ''}`}
                  disabled={!affordable}
                  onClick={() => gameStore.research(view.def.id)}
                >
                  Work it out
                </button>
              </div>
            </article>
          );
        })}
      </div>

      {known.length > 0 ? (
        <>
          <h3 className="ancestral-tier-label" style={{ marginTop: 18 }}>
            Already known
          </h3>
          <div className="card-effects">
            {known.map((view) => (
              <span key={view.def.id} className="effect-chip" title={view.def.description}>
                {view.def.name}
              </span>
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
